import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Grid,
  keyframes,
  Container,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import { useFormik } from "formik";
import * as yup from "yup";

import { useCreateFormMutation } from "../../Store/api/rtk";
import { showGlobalToast } from "../ToastProvider";
import ImgBox from "../ImgBox";

// Keyframe Animations
const moveGrid = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: 40px 40px; }
`;

const pulseDot = keyframes`
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(0, 255, 204, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 8px rgba(0, 255, 204, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(0, 255, 204, 0); }
`;

// Validation Schema
const contactSchema = yup.object({
  name: yup
    .string()
    .required("نام و نام خانوادگی یا نام سازمان الزامی است")
    .min(3, "حداقل ۳ کاراکتر"),
  contact_info: yup.string().required("آدرس ایمیل یا شماره تماس الزامی است"),
  message: yup
    .string()
    .required("متن پیام نمی‌تواند خالی باشد")
    .min(10, "متن پیام باید حداقل ۱۰ کاراکتر باشد"),
});

// Custom Input Component
const PremiumInput = ({ label, name, formik, multiline = false }) => {
  const hasError = formik?.touched?.[name] && Boolean(formik?.errors?.[name]);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{
          color: hasError ? "#ff1744" : "rgba(255,255,255,0.7)",
          fontSize: 13,
          mb: 1,
          letterSpacing: "0.5px",
          fontWeight: 500,
          transition: "color 0.3s ease",
        }}
      >
        {label}
      </Typography>
      <Box
        component={multiline ? "textarea" : "input"}
        aria-label={label}
        rows={multiline ? 4 : 1}
        name={name}
        value={formik?.values?.[name]}
        onChange={formik?.handleChange}
        onBlur={formik?.handleBlur}
        sx={{
          width: "100%",
          bgcolor: "#0a0a0a",
          border: `1px solid ${hasError ? "#ff1744" : "rgba(255, 255, 255, 0.1)"}`,
          borderRadius: "12px",
          color: "#fff",
          fontSize: 15,
          fontFamily: "inherit",
          py: 1.8,
          px: 2,
          outline: "none",
          resize: "none",
          transition: "all 0.3s ease",
          boxSizing: "border-box",
          "&:hover:not(:focus)": {
            borderColor: hasError ? "#ff1744" : "rgba(255, 255, 255, 0.2)",
            bgcolor: "#0f0f0f",
          },
          "&:focus": {
            bgcolor: "#000",
            borderColor: hasError ? "#ff1744" : "#00ffcc",
            boxShadow: hasError
              ? "0 0 0 4px rgba(255, 23, 68, 0.1)"
              : "0 0 0 4px rgba(0, 255, 204, 0.1)",
          },
        }}
      />
      {hasError && (
        <Typography sx={{ color: "#ff1744", fontSize: 12, mt: 0.8, pr: 0.5 }}>
          {formik?.errors?.[name]}
        </Typography>
      )}
    </Box>
  );
};

// Main Component
export default function ContactUs() {
  const navigate = useNavigate();
  const [createForm, { isLoading }] = useCreateFormMutation();

  const contactInfos = [
    {
      icon: <EmailOutlinedIcon />,
      title: "پشتیبانی و فروش",
      value: "info@vira-tec.ir",
      href: "mailto:info@vira-tec.ir",
    },
    {
      icon: <CallOutlinedIcon />,
      title: "تماس مستقیم",
      value: "۰۹۳۰۸۸۰۶۳۵۳",
      href: "tel:۰۹۳۰۸۸۰۶۳۵۳",
    },
    {
      icon: <LocationOnOutlinedIcon />,
      title: "دفتر مرکزی",
      value:
        "مشهد، بلوار دستغیب، بین دستغیب ۳۳ و ۳۵، ساختمان  آتیه، طبقه ۳، واحد ۳۰۴",
    },
  ];

  const logos = [
    {
      src: "/media/global/e-namad.webp",
      name: "e-namad",
      link: "https://trustseal.enamad.ir/?id=733549&Code=sDwIhQenCijcCXTQ5PAad3Yhyeo4pN0P",
    },
  ];

  // Formik Initialization
  const formik = useFormik({
    initialValues: {
      name: "",
      contact_info: "",
      message: "",
    },
    validationSchema: contactSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const res = await createForm(values).unwrap();
        if (res?.status === "ok" || res?.data?.status === "ok") {
          showGlobalToast("پیام شما با موفقیت ارسال شد", "success");
          resetForm();
        }
      } catch (error) {
        console.error(error);
        showGlobalToast(
          error?.data?.message || "خطایی در ارسال پیام رخ داد",
          "error",
        );
      }
    },
  });

  // Mapped Elements
  const contactInfoItems = contactInfos?.map((item, index) => (
    <Box
      component={"a"}
      href={item?.href || ""}
      key={index}
      sx={{
        display: "flex",
        gap: 3,
        p: 2,
        borderRadius: "16px",
        color: "white",
        textDecoration: "none",
        bgcolor: "transparent",
        border: "1px solid transparent",
        transition: "all 0.3s ease",
        cursor: "pointer",
        "&:hover": {
          bgcolor: "rgba(255,255,255,0.02)",
          border: "1px solid rgba(255,255,255,0.08)",
          transform: "translateX(-5px)",
          "& .contact-icon-box": {
            bgcolor: "#fff",
            color: "#000",
          },
        },
      }}
    >
      <Box>
        <Box
          className="contact-icon-box"
          sx={{
            width: 48,
            height: 48,
            borderRadius: "12px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "rgba(255,255,255,0.8)",
            transition: "all 0.3s ease",
          }}
        >
          {item?.icon}
        </Box>
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: 12,
            color: "rgba(255,255,255,0.5)",
            mb: 0.5,
            letterSpacing: "0.5px",
          }}
        >
          {item?.title}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 14, sm: 16 },
            fontWeight: 600,
            letterSpacing: "1px",
          }}
        >
          {item?.value}
        </Typography>
      </Box>
    </Box>
  ));

  const logoItems = logos?.map((logo, index) => (
    <Box
      key={index}
      component="a"
      href={logo?.link}
      referrerPolicy="origin"
      target="_blank"
      rel="noopener"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "10px",
        transition: "all 0.3s ease",
        border: "1px solid rgba(255,255,255,0.08)",
        p: "12px",

        "& img": {
          filter: "grayscale(100%)",
          transition: "all 0.3s ease",
        },

        "&:hover": {
          bgcolor: "#fff",
          border: "1px solid rgba(255,255,255,0.08)",
          "& img": {
            filter: "brightness(0)",
          },
        },
      }}
    >
      <ImgBox
        src={logo?.src}
        alt={logo?.name}
        sx={{
          height: "30px",
          width: "30px",
          objectFit: "contain",
          display: "block",
        }}
      />
    </Box>
  ));

  return (
    <Box id={"contact-us"} sx={{ bgcolor: "#030303" }}>
      <Container
        maxWidth="xxl"
        sx={{
          position: "relative",
          py: { xs: 8, md: 15 },
          px: { xs: 2, md: 6, lg: 10 },
          color: "#fff",
          overflow: "hidden",
          "& *": {
            direction: "rtl",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundSize: "40px 40px",
            animation: `${moveGrid} 4s linear infinite`,
            maskImage:
              "radial-gradient(ellipse at top, black 0%, transparent 80%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at top, black 0%, transparent 80%)",
            zIndex: 0,
            pointerEvents: "none",
          }}
        />

        {/* Employment Invitation Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2.5,
            mb: 10,
            borderRadius: "16px",
            bgcolor: "rgba(0, 255, 204, 0.03)",
            border: "1px dashed rgba(0, 255, 204, 0.3)",
            transition: "all 0.3s ease",
            "&:hover": {
              bgcolor: "rgba(0, 255, 204, 0.06)",
              borderColor: "rgba(0, 255, 204, 0.5)",
            },
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: "8px", md: "0" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: "rgba(0, 255, 204, 0.1)",
                color: "#00ffcc",
              }}
            >
              <WorkOutlineOutlinedIcon />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#fff",
                  mb: 0.5,
                }}
              >
                به تیم توسعه ما بپیوندید
              </Typography>
              <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
                فرصتی برای رشد، شکوفایی و خلق ارزش در محیطی پویا
              </Typography>
            </Box>
          </Box>

          <Box
            component="button"
            onClick={() => navigate("/employment-form")}
            sx={{
              bgcolor: "#00ffcc",
              color: "#000",
              border: "none",
              borderRadius: "10px",
              py: 1,
              px: 2,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.3s ease",
              fontFamily: "inherit",
              "&:hover": {
                bgcolor: "#fff",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0, 255, 204, 0.2)",
              },
            }}
          >
            ارسال پروفایل
          </Box>
        </Box>
        <Grid
          container
          spacing={{ xs: 6, lg: 8 }}
          position="relative"
          zIndex={1}
        >
          {/* Right Column: Information & Employment */}
          <Grid size={{ xs: 12, lg: 5 }}>
            <Box mb={3}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  bgcolor: "rgba(0, 255, 204, 0.05)",
                  border: "1px solid rgba(0, 255, 204, 0.2)",
                  borderRadius: "100px",
                  py: 0.8,
                  px: 2,
                  mb: 4,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#00ffcc",
                    animation: `${pulseDot} 2s infinite`,
                  }}
                />
                <Typography
                  sx={{
                    color: "#00ffcc",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    mt: 0.5,
                  }}
                >
                  آماده برای شروع پروژه‌های جدید
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: { xs: 32, lg: 42 },
                  fontWeight: 900,
                  lineHeight: 1.2,
                  mb: 3,
                  letterSpacing: "-1.5px",
                }}
              >
                آینده را <span style={{ color: "#00ffcc" }}>خلق</span> کنید.
              </Typography>

              <Typography
                sx={{
                  opacity: 0.7,
                  fontSize: { xs: 15, md: 17 },
                  lineHeight: 1.8,
                  maxWidth: 420,
                  mb: 4,
                }}
              >
                فرقی نمی‌کند یک ایده خام دارید یا نیازمند توسعه یک پلتفرم در
                مقیاس سازمانی هستید؛ مهندسین ما آماده‌ی چالش‌های جدیدند.
              </Typography>
            </Box>

            <Stack mb={2} spacing={1}>
              {contactInfoItems}
            </Stack>

            <Stack
              spacing={1}
              sx={{
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              {logoItems}
            </Stack>
          </Grid>

          {/* Left Column: Form Section */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <Box
              component="form"
              onSubmit={formik?.handleSubmit}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: "24px",
                border: "1px solid rgba(255, 255, 255, 0.08)",
                borderTop: "1px solid rgba(0, 255, 204, 0.4)",
                boxShadow: "0 25px 50px -12px rgba(0,0,0,0.8)",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "60%",
                  height: "20%",
                  background:
                    "radial-gradient(ellipse, rgba(0,255,204,0.15) 0%, transparent 70%)",
                  pointerEvents: "none",
                }}
              />

              <Typography
                sx={{ fontSize: 24, fontWeight: 800, mb: 4, color: "#fff" }}
              >
                ارسال پیام
              </Typography>

              <PremiumInput
                label="نام و نام خانوادگی / سازمان"
                name="name"
                formik={formik}
              />
              <PremiumInput
                label="آدرس ایمیل یا شماره تماس"
                name="contact_info"
                formik={formik}
              />
              <PremiumInput
                label="چگونه می‌توانیم به شما کمک کنیم؟"
                name="message"
                formik={formik}
                multiline
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
                <Box
                  component="button"
                  type="submit"
                  disabled={isLoading}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    bgcolor: "#fff",
                    color: "#000",
                    border: "none",
                    borderRadius: "12px",
                    py: 1.8,
                    px: 4,
                    fontSize: 16,
                    fontWeight: 800,
                    cursor: isLoading ? "not-allowed" : "pointer",
                    opacity: isLoading ? 0.7 : 1,
                    transition: "all 0.3s ease",
                    boxShadow: "0 4px 14px 0 rgba(255,255,255,0.1)",
                    "&:hover": {
                      bgcolor: isLoading ? "#fff" : "#00ffcc",
                      transform: isLoading ? "none" : "translateY(-2px)",
                      boxShadow: isLoading
                        ? "none"
                        : "0 6px 20px rgba(0, 255, 204, 0.25)",
                      "& .arrow-icon": {
                        transform: isLoading ? "none" : "translateX(-6px)",
                      },
                    },
                    "&:active": {
                      transform: isLoading ? "none" : "translateY(0)",
                    },
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontFamily: "inherit" }}>
                    {isLoading ? "در حال ارسال..." : "ثبت درخواست"}
                  </Typography>
                  {!isLoading && (
                    <Box
                      className="arrow-icon"
                      sx={{
                        display: "flex",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      <ArrowBackIcon fontSize="small" />
                    </Box>
                  )}
                  {isLoading && (
                    <CircularProgress size={18} sx={{ color: "#000" }} />
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
